// What a decommission leaves behind (ENTRAID-307), for the operator. Pure and shared: the backend returns it with the
// decommission result (lib/decommission.mjs) and the operator UI lists it from the record (Settings, Deployment). It only
// describes; nothing here deletes anything. Identifiers are never part of the text.

const pendingKeyCount = (c) => {
  const v = c?.pendingRevokeKeyIds;
  if (Array.isArray(v)) return v.length;
  if (v instanceof Set) return v.size;
  return 0;
};

/**
 * True only for an EXPLICIT shared-app customer: no app ids AND legacyAppKeyId recorded (the shared-app model's field),
 * the same rule as the entitlement sweep. Never inferred from missing app ids alone.
 */
const isLegacy = (c) => Boolean(c) && !c.entraAppObjectId && !c.entraAppClientId && typeof c.legacyAppKeyId === 'string' && c.legacyAppKeyId.length > 0;

/**
 * True while an Entra credential may still be live on this record: a current key, a pending revoke or a recorded
 * failure, unless the customer's app was deleted (every credential went with it). Delete and tenant release refuse
 * while this holds (ENTRAID-307 review).
 */
export function credentialOwed(c) {
  if (!c || typeof c !== 'object') return false;
  if (c.entraAppDeletedAt && !c.entraAppObjectId) return false;
  return Boolean(c.appCredentialKeyId) || pendingKeyCount(c) > 0 || Boolean(c.revokeFailedAt);
}

/**
 * @param {object} customer - the customer record (as read after the decommission, or before it with `outcome`)
 * @param {{ revokeOk?: boolean, appDeleted?: boolean }} [outcome] - the decommission's own result, when at hand
 * @returns {{ id: string, label: string, detail: string }[]}
 */
export function decommissionLeftovers(customer, { revokeOk, appDeleted } = {}) {
  if (!customer || typeof customer !== 'object') return [];
  const out = [];
  // The decommission's own answer wins; otherwise the record says whether a key is still owed.
  const keyOwed = typeof revokeOk === 'boolean' ? !revokeOk : credentialOwed(customer);
  if (customer.entraTenantId) {
    out.push({ id: 'tenant-binding', label: 'Microsoft Entra tenant binding', detail: keyOwed
      ? 'The tenant stays reserved for this customer until the credential revoke is confirmed; it is then released automatically.'
      : 'The tenant stays reserved for this customer until it is released. It is released automatically; Release tenant does it now.' });
  }
  out.push({ id: 'idp-tenant', label: 'Sign-in tenant and memberships', detail: 'The customer tenant and its members on the IPC Global sign-in are kept. Auditor roles are removed.' });
  if (isLegacy(customer)) {
    if (keyOwed) out.push({ id: 'legacy-shared-app-key', label: 'Key on the retired shared app', detail: 'It cannot be revoked per customer. It goes when the shared app credentials are deleted at the end of the legacy cutover.' });
  } else {
    if (keyOwed) out.push({ id: 'entra-credential', label: 'Entra app credential', detail: 'The revoke has not been confirmed. The daily sweep retries it.' });
    if (appDeleted !== true && customer.entraAppObjectId) out.push({ id: 'entra-app', label: 'Customer Entra app', detail: 'Not deleted yet: the app is deleted only after every credential is confirmed revoked.' });
  }
  return out;
}
